import { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * themeTokens (ST-00127)
 * ----------------------------------------------------------------------------
 * Bridges the CSS token layer into JS for libraries that cannot consume
 * `var(--…)` — chiefly recharts, which writes SVG presentation attributes
 * (stroke="…", fill="…") rather than CSS declarations.
 *
 * Usage in a chart:
 *
 *   const t = useThemeTokens();
 *   <CartesianGrid stroke={t.grid} />
 *   <XAxis stroke={t.axis} tick={{ fill: t.axisLabel, fontSize: 12 }} />
 *   <Tooltip contentStyle={{ background: t.tooltipBg, border: `1px solid ${t.border}`, color: t.text }} />
 *
 * The map is memoised on `resolvedTheme`, so every consumer re-reads the
 * computed values — and therefore re-renders — the moment the theme flips.
 */

/** Read a single CSS custom property off <html>. Returns '' when unavailable. */
export const getToken = (name) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return '';
  const prop = name.startsWith('--') ? name : `--${name}`;
  return getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
};

/**
 * Read a token as an `rgb(r g b / alpha)` string. Only works for the RGB-triplet
 * tokens (e.g. `--accent-rgb`, `--shadow-rgb`, `--tw-gray-200`).
 */
export const getTokenAlpha = (name, alpha) => {
  const triplet = getToken(name);
  return triplet ? `rgb(${triplet} / ${alpha})` : '';
};

/**
 * The token set charts and other canvas/SVG surfaces need. Keys are semantic
 * (role-based) — pick by what the value IS, not by which hex it used to be.
 */
const readTokens = () => ({
  // Surfaces
  surface: getToken('--surface'),
  surfaceRaised: getToken('--surface-raised'),
  surfaceSunken: getToken('--surface-sunken'),
  surfaceHover: getToken('--surface-hover'),
  appBg: getToken('--app-bg'),

  // Text
  textStrong: getToken('--text-strong'),
  text: getToken('--text'),
  textMuted: getToken('--text-muted'),
  textFaint: getToken('--text-faint'),
  textOnAccent: getToken('--text-on-accent'),

  // Lines
  border: getToken('--border'),
  borderStrong: getToken('--border-strong'),
  borderSubtle: getToken('--border-subtle'),

  // Chart furniture — axes, gridlines, reference marks, cursor highlight
  axis: getToken('--border-strong'),
  axisLabel: getToken('--text-muted'),
  grid: getToken('--border'),
  gridSubtle: getToken('--border-subtle'),
  reference: getToken('--text-faint'),
  cursor: getTokenAlpha('--accent-rgb', 0.08),

  // Tooltip / popover chrome
  tooltipBg: getToken('--surface-raised'),
  tooltipBorder: getToken('--border'),
  tooltipText: getToken('--text-strong'),

  // Accent + status (series colors stay per-chart; these are for UI marks)
  accent: getToken('--accent'),
  accentRgb: getToken('--accent-rgb'),
  accentText: getToken('--accent-text'),
  accentSoft: getToken('--accent-soft'),
  accentBorder: getToken('--accent-border'),
  danger: getToken('--danger'),
  warning: getToken('--warning'),
  success: getToken('--success'),
  info: getToken('--info'),

  // Elevation
  overlay: getToken('--overlay'),
  shadowRgb: getToken('--shadow-rgb'),
});

/**
 * Resolved theme tokens, refreshed on every theme change.
 * @returns {ReturnType<typeof readTokens> & { theme: 'light' | 'dark', isDark: boolean }}
 */
export const useThemeTokens = () => {
  const { resolvedTheme } = useTheme();

  return useMemo(
    () => ({
      ...readTokens(),
      theme: resolvedTheme,
      isDark: resolvedTheme === 'dark',
    }),
    [resolvedTheme]
  );
};

export default useThemeTokens;
